/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'
import http_requestImg from 'assets/images/http-request.png'

export const _3 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>3 - AJAX и Fetch API</h2>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.1 - Asynchronous JavaScript and XML</h3>
      <p className={styles.description}>
        <b>AJAX</b> — метод отправки или запроса данных с последующим обновлением интерфейса по этим данным, без
        необходимости перезагрузки страницы. За счет этого уменьшается время отклика и веб-страница становится более
        интерактивной.
      </p>
      <p className={styles.description}>
        Несмотря на то, что в названии технологии присутствует XML, использовать его вовсе не обязательно. Под AJAX
        подразумевают любое общение с сервером без перезагрузки страницы.
      </p>
      <p className={styles.description}>В пример можно привести подгрузку данных: </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          На веб-странице происходит событие (страница загружается, нажимается кнопка "Показать больше", отправляется
          форма и т. п.).
        </li>
        <li className={styles.list__item}>
          На клиенте, с помощью JavaScript, реакцией на это событие выполнятся функция для работы с сервером, где
          создается и отправляется HTTP-запрос.
        </li>
        <li className={styles.list__item}>
          Сервер получает и обрабатывает HTTP-запрос, отправляя обратно в ответе данные в JSON-формате.
        </li>
        <li className={styles.list__item}>
          На клиенте, с помощью JavaScript, ответ от сервера обрабатывается, считываются данные и обновляется интерфейс.
        </li>
      </ul>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.2 - Fetch API</h3>
      <p className={styles.description}>
        <a
          className={styles.link}
          href="https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API"
          target="_blank"
          rel="noopener noreferrer"
        >
          Fetch API{' '}
        </a>
        — предоставляет интерфейс, набор методов и свойств для отправки, получения и обработки ресурсов от сервера.{' '}
      </p>
      <p className={styles.description}>
        Это{' '}
        <a
          className={styles.link}
          href="https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest"
          target="_blank"
          rel="noopener noreferrer"
        >
          XMLHttpRequest{' '}
        </a>
        нового поколения. Он предоставляет улучшенный интерфейс для составления запросов к серверу и построен на
        обещаниях (promise).{' '}
      </p>
      <CopyBlock text={`fetch(url, options)`} language="javascript" showLineNumbers={false} theme={ocean} />
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <code className={styles.code}>url</code> — обязательный параметр - путь к данным которые вы хотите получить.
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>options</code> — необязательный параметр - объект настроек запроса. Содержит
          служебную информацию: метод (по умолчанию <code className={styles.code}>GET</code>), заголовки, тело и т. д.
        </li>
        <li className={styles.list__item}>Возвращает промис, который содержит ответ сервера.</li>
      </ul>
      <CopyBlock
        text={`fetch('https://jsonplaceholder.typicode.com/users')
  .then(response => {
    //response handling
  })
  .then(data => {
    // data handling
  })
  .catch(error => {
    // error handling
  });`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <section className={styles.subsection}>
        <h4 className={styles.h4}>3.2.1 - Response</h4>
        <p className={styles.description}>
          В первый <code className={styles.code}>then</code> передается экземпляр класса{' '}
          <a
            className={styles.link}
            href="https://developer.mozilla.org/en-US/docs/Web/API/Response"
            target="_blank"
            rel="noopener noreferrer"
          >
            Response
          </a>
          , снабженный различными методами и свойствами. В нем содержится служебная информация о состоянии ответа
          сервера.
        </p>
        <p className={styles.description}>
          В зависимости от типа получаемого контента, используется различный метод парса.
        </p>
        <ul className={styles.list}>
          <li className={styles.list__item}>
            <code className={styles.code}>Response.prototype.json()</code> - используется, когда от бекенда ожидаются
            данные в JSON-формате.
          </li>
          <li className={styles.list__item}>
            <code className={styles.code}>Response.prototype.text()</code> - используется, когда от бекенда ожидаются
            данные в простом текстовом формате, например <code className={styles.code}>.csv</code> - формат хранения
            табличных данных в виде текстового файла.
          </li>
          <li className={styles.list__item}>
            <code className={styles.code}>Response.prototype.blob()</code> - используется, когда от бекенда ожидаются
            данные, описывающие файл, например изображение, аудио или видео.
          </li>
        </ul>
        <CopyBlock
          text={`fetch('https://jsonplaceholder.typicode.com/users')
  .then(response => {
    return response.json();
  })
  .then(data => {
    // data handling
  })
  .catch(error => {
    // error handling
  });`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>3.2.2 - Headers</h4>
        <p className={styles.description}>
          <a
            className={styles.link}
            href="https://developer.mozilla.org/en-US/docs/Web/API/Headers"
            target="_blank"
            rel="noopener noreferrer"
          >
            Headers{' '}
          </a>
          — позволяет выполнять различные действия в заголовках HTTP-запроса и ответа. Эти действия включают в себя
          извлечение, настройку, добавление и удаление заголовков (методы <code className={styles.code}>append</code>,{' '}
          <code className={styles.code}>has</code>, <code className={styles.code}>get</code>,{' '}
          <code className={styles.code}>set</code>, <code className={styles.code}>delete</code>).
        </p>
        <CopyBlock
          text={`const headers = new Headers({
  'Content-Type': 'application/json',
  'X-Custom-Header': 'custom value',
});

headers.append('Content-Type', 'text/bash');
headers.append('X-Custom-Header', 'custom value');
headers.has('Content-Type'); // true
headers.get('Content-Type'); // "text/bash"
headers.set('Content-Type', 'application/json');
headers.delete('X-Custom-Header');`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>
          Для составления заголовков запроса можно использовать просто литерал объекта со свойствами. В таком случае
          методов заголовков не будет, что зачастую и не требуется.{' '}
        </p>
        <CopyBlock
          text={`const headers = {
  'Content-Type': 'application/json',
  'X-Custom-Header': 'custom value',
};`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>Запрос с использованием заголовков может выглядеть так. </p>
        <CopyBlock
          text={`fetch('https://jsonplaceholder.typicode.com/users', {
  method: 'GET',
  headers: {
    Accept: 'application/json',
  },
}).then(response => {});`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>3.2.3 - Ошибка при работе с асинхронным кодом</h4>
        <p className={styles.description}>
          Разберем распространенную ошибку начинающих разработчиков, попытку использовать данные вне метода{' '}
          <code className={styles.code}>then</code>, используем для этого предыдущий пример.
        </p>
        <iframe
          height="300"
          scrolling="no"
          title="module-12-async-call-mistake"
          src="https://codepen.io/goit-fe-adv/embed/veOrOa?default-tab=html%2Cresult"
          frameBorder="no"
          loading="lazy"
          allowTransparency="true"
          allowFullScreen="true"
        />
      </section>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.3 - Вкладка Network</h3>
      <p className={styles.description}>
        На вкладке <code className={styles.code}>Network</code> отображаются все выполняющиеся на странице HTTP-запросы.
        Выбрав <code className={styles.code}>XHR</code> (можно фильтровать по типу запроса), останутся только запросы к
        бекенду, и после нажатия кнопки, через некоторое время, отобразится запрос. Выбрав его, можно посмотреть
        служебную информацию и тело ответа на вкладках <code className={styles.code}>Headers</code>,{' '}
        <code className={styles.code}>Preview</code>, <code className={styles.code}>Response</code> и{' '}
        <code className={styles.code}>Timing</code>.
      </p>
      <iframe
        height="300"
        scrolling="no"
        title="module-12-simple-fetch"
        src="https://codepen.io/goit-fe-adv/embed/gGaaqg?default-tab=html%2Cresult"
        frameBorder="no"
        loading="lazy"
        allowTransparency="true"
        allowFullScreen="true"
      />
      <img className={styles.img} src={http_requestImg} alt="http-request" />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.4 - Работа с публичным RESTful API</h3>
      <p className={styles.description}>
        Каждый API уникален, их тысячи, невозможно заучить код для работы с одним сервисом и использовать его для
        общения с другим. С другой стороны, RESTful API построены по принципам REST-архитектуры, а это значит, что можно
        понять принцип и методы работы, после чего, все что нужно сделать — это ознакомиться с документацией того
        сервиса, который необходимо использовать.{' '}
      </p>
      <p className={styles.description}>
        Для примера был выбран{' '}
        <a className={styles.link} href="https://api.privatbank.ua/" target="_blank" rel="noopener noreferrer">
          API ПриватБанка
        </a>
        . Из документации берем URL для запроса информации о текущем курсе обмена валют.
      </p>
      <CopyBlock
        text={`https://api.privatbank.ua/p24api/pubinfo?exchange&json&coursid=11`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>Состоит из: </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <code className={styles.code}>https://api.privatbank.ua/p24api</code> — endpoint, base URL, точка входа на
          API.
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>/pubinfo</code> — ресурс, к которому мы обращаемся
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>?</code> — говорит о том, что дальше идут параметры запроса
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>&amp;</code> — используется для указания смыслового{' '}
          <code className={styles.code}>И</code>, связывает параметры запроса
        </li>
      </ul>
      <p className={styles.description}>Параметры ответа из документации: </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <code className={styles.code}>ccy</code> - код валюты
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>base_ccy</code> - код национальной валюты
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>buy</code> - курс покупки
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>sale</code> - курс продажи
        </li>
      </ul>
      <iframe
        height="300"
        scrolling="no"
        title="module-12-privat-api"
        src="https://codepen.io/goit-fe-adv/embed/JrYGVE?default-tab=html%2Cresult"
        frameBorder="no"
        loading="lazy"
        allowTransparency="true"
        allowFullScreen="true"
      />
    </section>
  </section>
))
